import { Tooltip } from '@mantine/core'
import {
  IconAlertCircle,
  IconCircleCheck,
  IconClock,
  IconX,
} from '@/components/icons'

export type ChannelDeliveryStatus = 'pending' | 'sent' | 'failed' | 'skipped'

export const OUTBOUND_CHANNEL_TYPES = new Set([
  'telegram',
  'whatsapp',
  'email',
  'connector',
  'widget',
  'webchat',
])

type Props = {
  status: ChannelDeliveryStatus
  /** Проект с переводом — «доставлено» значит ушёл перевод клиенту */
  translationEnabled?: boolean
  /** Мозгобот или оператор — подсказка в тултипе */
  senderKind?: 'operator' | 'ai'
  deliveryError?: string | null
  size?: number
}

const META: Record<
  ChannelDeliveryStatus,
  {
    Icon: typeof IconClock
    color: string
    label: (tr: boolean) => string
  }
> = {
  pending: {
    Icon: IconClock,
    color: 'var(--mantine-color-gray-6)',
    label: () => 'Отправка в канал…',
  },
  sent: {
    Icon: IconCircleCheck,
    color: 'var(--mantine-color-teal-6)',
    label: (tr) =>
      tr ? 'Перевод доставлен клиенту' : 'Доставлено клиенту',
  },
  failed: {
    Icon: IconAlertCircle,
    color: 'var(--mantine-color-red-6)',
    label: (tr) =>
      tr ? 'Перевод не отправлен — клиент оригинал не получил' : 'Не доставлено в канал клиента',
  },
  skipped: {
    Icon: IconX,
    color: 'var(--mantine-color-gray-5)',
    label: () => 'Отправка пропущена (нет адресата канала)',
  },
}

export function MessageChannelDeliveryStatus({
  status,
  translationEnabled = false,
  senderKind,
  deliveryError,
  size = 14,
}: Props) {
  const meta = META[status]
  const Icon = meta.Icon
  const base = meta.label(translationEnabled)
  const label =
    senderKind === 'ai' ? `Мозгобот: ${base}` : senderKind === 'operator' ? `Оператор: ${base}` : base
  const tooltip = deliveryError ? `${label}. ${deliveryError}. Отправьте сообщение повторно.` : label
  return (
    <Tooltip label={tooltip} withArrow position="top">
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          color: meta.color,
          lineHeight: 0,
        }}
        aria-label={tooltip}
      >
        <Icon size={size} stroke={1.75} />
      </span>
    </Tooltip>
  )
}

export function isChannelDeliveryStatus(
  raw: string | null | undefined,
): raw is ChannelDeliveryStatus {
  return raw === 'pending' || raw === 'sent' || raw === 'failed' || raw === 'skipped'
}
